/**
 * Cadastro e renovação do celular com passkey (US-3101, US-3102) — página pública aberta no próprio celular — Nexora Grid.
 *
 * Criado em: 25/10/2026 · Última modificação: 25/10/2026 · Versão: 1.1.0
 *
 * `/cadastrar-aparelho#<token>`: o link vem do bot (Telegram/WhatsApp) ou do CCO › Assistente. O token fica no
 * fragmento (`#`), que o navegador não envia ao servidor nem grava em log de proxy. A página pede as opções, cria a
 * passkey no chip seguro do aparelho (biometria ou PIN obrigatórios) e envia só a chave pública ao servidor.
 * Não precisa de login: o link de uso único identifica o usuário e o vínculo.
 *
 * v1.1 (US-3102): com `tipo = "autenticar"` (cadastro vencido), o aparelho ASSINA com a passkey que já tem
 * (`navigator.credentials.get`) e o servidor renova o vencimento — não cria chave nova.
 */
import { useEffect, useState, type FormEvent } from "react";
import { api, ErroApi } from "../api";
import { Logomarca } from "../marca";

type Opcoes = { tipo: "criar" | "autenticar"; opcoes: Record<string, any>; usuario: string; canal: string; conta: string; aparelho?: string };

export const deB64url = (t: string) => Uint8Array.from(atob(t.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (t.length % 4)) % 4)), (c) => c.charCodeAt(0));
export const paraB64url = (b: ArrayBuffer) => btoa(String.fromCharCode(...new Uint8Array(b))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const ids = (lista: any[] | undefined) => (lista ?? []).map((c: any) => ({ ...c, id: deB64url(c.id) }));

/** Cria (cadastro) ou usa (assinatura) a passkey do aparelho e devolve a resposta em JSON base64url para o servidor. */
export async function usarPasskey(tipo: Opcoes["tipo"], o: Record<string, any>): Promise<Record<string, unknown>> {
  if (tipo === "autenticar") {
    const publicKey = { ...o, challenge: deB64url(o.challenge), allowCredentials: ids(o.allowCredentials) } as unknown as PublicKeyCredentialRequestOptions;
    const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
    if (!cred) throw new Error("O celular não assinou.");
    const r = cred.response as AuthenticatorAssertionResponse;
    return { id: cred.id, rawId: paraB64url(cred.rawId), type: cred.type, clientExtensionResults: cred.getClientExtensionResults(),
      authenticatorAttachment: (cred as any).authenticatorAttachment ?? undefined,
      response: { clientDataJSON: paraB64url(r.clientDataJSON), authenticatorData: paraB64url(r.authenticatorData),
        signature: paraB64url(r.signature), userHandle: r.userHandle ? paraB64url(r.userHandle) : undefined } };
  }
  const publicKey = { ...o, challenge: deB64url(o.challenge), user: { ...o.user, id: deB64url(o.user.id) },
    excludeCredentials: ids(o.excludeCredentials) } as unknown as PublicKeyCredentialCreationOptions;
  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error("O celular não criou a chave.");
  const r = cred.response as AuthenticatorAttestationResponse;
  return { id: cred.id, rawId: paraB64url(cred.rawId), type: cred.type, clientExtensionResults: cred.getClientExtensionResults(),
    authenticatorAttachment: (cred as any).authenticatorAttachment ?? undefined,
    response: { clientDataJSON: paraB64url(r.clientDataJSON), attestationObject: paraB64url(r.attestationObject),
      transports: typeof r.getTransports === "function" ? r.getTransports() : undefined } };
}

/** Mensagem da falha do WebAuthn ou da API, em português. */
export function mensagemDaFalha(falha: unknown): string {
  if (falha instanceof ErroApi) return falha.message;
  if (falha instanceof DOMException && falha.name === "NotAllowedError") return "Cancelado ou sem confirmação da biometria/PIN. Tente de novo.";
  if (falha instanceof DOMException && falha.name === "InvalidStateError") return "Este celular já está cadastrado na sua conta.";
  return falha instanceof Error ? falha.message : "Falha ao usar a chave do celular.";
}

export function CadastroAparelho() {
  const token = window.location.hash.replace(/^#/, "");
  const [dados, setDados] = useState<Opcoes | null>(null);
  const [nome, setNome] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [feito, setFeito] = useState<string | null>(null);
  const [esperando, setEsperando] = useState(false);
  const suportado = typeof window.PublicKeyCredential !== "undefined" && window.isSecureContext;
  const renovar = dados?.tipo === "autenticar";

  useEffect(() => {
    if (!token) { setErro("Link incompleto. Abra o link exatamente como chegou no chat."); return; }
    api<Opcoes>("/v1/aparelhos/cadastro/opcoes", { metodo: "POST", corpo: { token } })
      .then(setDados).catch((e) => setErro(e instanceof ErroApi ? e.message : "Servidor indisponível."));
  }, [token]);

  const cadastrar = async (e: FormEvent) => {
    e.preventDefault();
    setErro(null);
    setEsperando(true);
    try {
      // opções novas a cada tentativa: o desafio vale uma vez
      const novas = await api<Opcoes>("/v1/aparelhos/cadastro/opcoes", { metodo: "POST", corpo: { token } });
      const credencial = await usarPasskey(novas.tipo, novas.opcoes);
      const fim = await api<{ texto: string }>("/v1/aparelhos/cadastro/concluir", { metodo: "POST", corpo: { token, credencial, nome_aparelho: nome.trim() } });
      setFeito(fim.texto);
    } catch (falha) {
      setErro(mensagemDaFalha(falha));
    } finally {
      setEsperando(false);
    }
  };

  return (
    <div className="nx-tela tela-login" data-tema="cco">
    <main className="nx-cartao cartao-login form-conta" aria-label={renovar ? "Renovar este celular" : "Cadastrar este celular"}>
      <Logomarca className="logo-login" />
      <h2>{renovar ? "Renovar este celular" : "Cadastrar este celular"}</h2>
      {feito ? <p role="status">{feito}</p> : (
        <form onSubmit={cadastrar} className="form-conta">
          {dados && !renovar && (
            <p>{dados.usuario}, este celular vai ficar ligado ao seu {dados.canal} ({dados.conta}). A chave fica guardada só
              neste aparelho e é liberada pela sua biometria ou PIN — ninguém usa o assistente em seu nome sem ele.</p>
          )}
          {dados && renovar && (
            <p>{dados.usuario}, o cadastro do aparelho "{dados.aparelho}" no seu {dados.canal} ({dados.conta}) venceu. Confirme com a
              biometria ou o PIN para renovar — a chave continua a mesma.</p>
          )}
          {!suportado && <p className="erro" role="alert">Este navegador não usa passkey. Abra o link no navegador padrão do celular (Chrome ou Safari), em endereço https.</p>}
          {!renovar && (
            <div className="nx-campo">
              <label htmlFor="nome-aparelho">Nome do aparelho (opcional)</label>
              <input id="nome-aparelho" maxLength={60} placeholder="ex.: celular do plantão" value={nome} onChange={(e) => setNome(e.target.value)} />
            </div>
          )}
          {erro && <p className="erro" role="alert">{erro}</p>}
          <button type="submit" className="nx-botao nx-botao-principal" disabled={!dados || !suportado || esperando}>
            {esperando ? "Confirme no celular…" : renovar ? "Renovar com biometria ou PIN" : "Cadastrar com biometria ou PIN"}</button>
        </form>
      )}
    </main>
    </div>
  );
}
